import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { CONFIG_TOKEN } from 'config-token';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, first, mergeMap, switchMap } from 'rxjs/operators';
import { Config } from '../../config';
import { AuthService } from '../services/auth.service';

enum HttpResponseStatus {
  Unauthorized = 401,
  Forbidden = 403,
}

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  public constructor(private readonly authService: AuthService, @Inject(CONFIG_TOKEN) public config: Config) {}

  public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const urlDomainRegex = /^(?:\/\/|[^\/]+)*/;
    const requestDomain = request.url.match(urlDomainRegex)[0];

    if (!requestDomain.includes(this.config.editorBackendRootUrl)) {
      return next.handle(request);
    }

    return this.addToken$(request).pipe(
      first(),
      mergeMap(
        (requestWithToken: HttpRequest<any>): Observable<HttpEvent<any>> =>
          next.handle(requestWithToken).pipe(
            catchError((error: HttpErrorResponse) => {
              return this.handleResponseError$(error, request, next);
            })
          )
      )
    );
  }

  private addToken$(request: HttpRequest<any>): Observable<HttpRequest<any>> {
    return this.authService.idToken$.pipe(
      mergeMap((token: string): Observable<HttpRequest<any>> => {
        request = request.clone({
          headers: request.headers.set('Authorization', `Bearer ${token}`),
        });
        return of(request);
      })
    );
  }

  private handleResponseError$(error: HttpErrorResponse, request?: HttpRequest<any>, next?: HttpHandler): Observable<HttpEvent<any>> {
    if (error.status === HttpResponseStatus.Unauthorized || error.status === HttpResponseStatus.Forbidden) {
      return from(this.authService.refreshToken(true)).pipe(
        switchMap(() => {
          return this.addToken$(request).pipe(
            first(),
            mergeMap((requestWithToken: HttpRequest<any>): Observable<HttpEvent<any>> => next.handle(requestWithToken))
          );
        }),
        catchError((e) => {
          if (e.status === HttpResponseStatus.Unauthorized || e.status === HttpResponseStatus.Forbidden) {
            this.authService.logout();
            console.warn('logout', e.status, e);
          } else {
            return this.handleResponseError$(e);
          }
        })
      );
    }
    return throwError(error);
  }
}
